import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Reorder } from "framer-motion";
import { useLocation, Link, useRoute } from "wouter";
import { ArrowLeft, Plus, X, GripVertical, MapPin, Zap, Home, ExternalLink, Save, Share2, Lock, Loader2, ArrowUpDown, RefreshCw, Clock, Trash2, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LimitBanner } from "@/components/LimitBanner";
import { UpgradeModal } from "@/components/UpgradeModal";
import { OSMRouteMap } from "@/components/OSMRouteMap";
import { generateOSMRoute } from "@/lib/osmDirectionsService";
import { CustomAddressSelector, type AddressSelection } from "@/components/CustomAddressSelector";
import { useAuth } from "@/lib/authContext";
import { useRouteBuilder } from "@/contexts/RouteBuilderContext";
import { dummyShops } from "@/lib/dummyData";
import { FREE_TIER_LIMITS, PRO_TIER_LIMITS } from "@shared/schema";
import type { Shop } from "@shared/schema";
import { PrivacyWarningDialog } from "@/components/PrivacyWarningDialog";
import { getStaticMapUrl } from "@/lib/googleMaps";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

import { getOpeningStatus, type OpeningHourEntry } from "@/lib/openingHours";
import { useI18n } from "@/lib/i18n";
import { trackRouteEvent } from "@/hooks/useSessionTracker";
import { checkUsageLimit, incrementUsage } from "@/lib/usageLimits";
import { Footer } from "@/components/Footer";

export default function RouteBuilderPage() {
  const [, navigate] = useLocation();
  const { isLoggedIn, tier, maxStopsPerRoute, canUseHomeFeature, user, isInsider } = useAuth();
  const { t, locale } = useI18n();
  const {
    shops: preSelectedShops,
    startAddress: persistedStart,
    endAddress: persistedEnd,
    extraStop: persistedExtraStop,
    setStartAddress: setPersistedStart,
    setEndAddress: setPersistedEnd,
    setExtraStop: setPersistedExtraStop,
    clearRoute,
    hasAutoPopulatedHome,
    setHasAutoPopulatedHome,
    setShops: setContextShops,
    isCopiedRoute,
    originalCopiedShopIds
  } = useRouteBuilder();
  // Note: clearRoute is still used in save/share mutations (onSuccess callbacks)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeVariant, setUpgradeVariant] = useState<"home" | "routes">("home");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Grouped all route hooks at the top to prevent TDZ error
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedStops, setSelectedStops] = useState<Shop[]>(preSelectedShops);
  const [searchQuery, setSearchQuery] = useState("");
  const [startAddress, setStartAddress] = useState<AddressSelection | null>(persistedStart);
  const [endAddress, setEndAddress] = useState<AddressSelection | null>(persistedEnd);
  const [extraStop, setExtraStop] = useState<AddressSelection | null>(persistedExtraStop || null);
  const [showPrivacyWarning, setShowPrivacyWarning] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [routeChanged, setRouteChanged] = useState(false);
  const [autoOptimizeEnabled, setAutoOptimizeEnabled] = useState(true);
  const [selectedDay, setSelectedDay] = useState<number>(() => {
    const jsDay = new Date().getDay();
    return jsDay === 0 ? 6 : jsDay - 1;
  });
  const [routeMetrics, setRouteMetrics] = useState<{ distance: string; duration: string; polyline?: string } | null>(null);
  const [highlightedStopId, setHighlightedStopId] = useState<string | null>(null);
  const [needsRecalculation, setNeedsRecalculation] = useState(false);
  const [showMergePrompt, setShowMergePrompt] = useState(false);
  const [matchEdit, paramsEdit] = useRoute("/routes/edit/:id");
  const editRouteId = matchEdit && paramsEdit ? paramsEdit.id : null;
  const [hasPopulated, setHasPopulated] = useState(false);

  const copiedRouteDiffCount = useMemo(() => {
    if (!isCopiedRoute || !originalCopiedShopIds || originalCopiedShopIds.length === 0) return 0;

    const currentIds = new Set(selectedStops.map(s => s.id));
    const originalIds = new Set(originalCopiedShopIds);

    let count = 0;
    currentIds.forEach(id => {
      if (!originalIds.has(id)) count++;
    });
    originalIds.forEach(id => {
      if (!currentIds.has(id)) count++;
    });

    return count;
  }, [isCopiedRoute, originalCopiedShopIds, selectedStops]);

  const { data: existingRoute } = useQuery({
    queryKey: ['route', editRouteId],
    queryFn: async () => {
      if (!editRouteId) return null;
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) return null;

      const res = await fetch(`/api/routes/${editRouteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to fetch route");
      return res.json();
    },
    enabled: !!editRouteId
  });

  const handleMergeAction = (action: 'merge' | 'overwrite' | 'cancel') => {
    setShowMergePrompt(false);
    if (action === 'cancel') {
      navigate('/routes');
      return;
    }

    if (existingRoute) {
      // For 'overwrite' (Nee), immediately clear the pending shops from context
      // before populating with the existing route data. Use setContextShops([])
      // instead of clearRoute() so that we don't wipe context addresses we are
      // about to set via the local state setters below.
      if (action === 'overwrite') {
        setContextShops([]);
      }

      setName(existingRoute.name);
      setDescription(existingRoute.description || "");

      let finalStops = existingRoute.stopsData || [];
      const uniqueFinalStops = [];
      const seenIdsFinal = new Set();
      for (const s of finalStops) {
        if (!seenIdsFinal.has(s.id)) {
          seenIdsFinal.add(s.id);
          uniqueFinalStops.push(s);
        }
      }
      finalStops = uniqueFinalStops;

      if (action === 'merge') {
        const existingShopIds = new Set(finalStops.map((s: Shop) => s.id));
        const newShopsToAdd = preSelectedShops.filter(s => !existingShopIds.has(s.id));
        finalStops = [...finalStops, ...newShopsToAdd];
      }

      setSelectedStops(finalStops);
      setStartAddress(existingRoute.start_address);
      setEndAddress(existingRoute.end_address);
      setExtraStop(existingRoute.extra_stop);

      const distance = existingRoute.osm_route_data?.distance || existingRoute.distance;
      const duration = existingRoute.osm_route_data?.duration;
      const polyline = existingRoute.osm_route_data?.polyline;
      if (distance || polyline) {
        setRouteMetrics({
          distance: distance || "",
          duration: duration || "",
          polyline: polyline || ""
        });
      }

      setHasPopulated(true);
      setRouteChanged(action === 'merge');

      if (!isInsider) {
        setAutoOptimizeEnabled(false);
      }
    }
  };


  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isSharingAction = useRef(false);

  const saveRouteMutation = useMutation({
    mutationFn: async (data: any) => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) throw new Error("Not authenticated");

      const method = data.id ? "PUT" : "POST";
      const url = data.id ? `/api/routes/${data.id}` : "/api/routes";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to save route");
      }

      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["routes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/routes/public"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      // Refresh the route detail so the map thumbnail reflects the latest save
      if (editRouteId) {
        queryClient.invalidateQueries({ queryKey: ['route', editRouteId] });
      } else if (data?.id) {
        queryClient.invalidateQueries({ queryKey: ['route', data.id] });
      }

      toast({
        title: t("routeBuilder.saveSuccessTitle"),
        description: t("routeBuilder.saveSuccessDesc"),
        duration: 3000,
      });

      setRouteChanged(false);
      // Handle navigation based on if it was a share action
      if (isSharingAction.current && data?.id) {
        isSharingAction.current = false;
        clearRoute();
        navigate(`/routes/${data.id}?share=true`);
      } else {
        // Navigate to edit page for new routes; clear context in both cases.
        if (!editRouteId && data?.id) {
          clearRoute();
          navigate(`/routes/edit/${data.id}`, { replace: true });
        } else if (editRouteId) {
          clearRoute();
        }
      }
    },
    onError: (error: Error) => {
      toast({
        title: t("routeBuilder.saveErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const performSave = async (isPublic: boolean, showToast = true) => {
    if (!name || selectedStops.length < 2 || saveRouteMutation.isPending) return;

    // Determine city/region for the route based on the first shop
    const primaryShop = selectedStops[0];
    const region = primaryShop?.province || "Nederland";
    const city = primaryShop?.city || "Diverse";

    // Format stops as { shopId, order }
    const stopsFormatted = selectedStops.map((s, i) => ({
      shopId: s.id,
      order: i + 1
    }));

    saveRouteMutation.mutate({
      id: editRouteId,
      name,
      description,
      shop_ids: selectedStops.map(s => s.id),
      stops: stopsFormatted,
      start_address: startAddress || null,
      end_address: endAddress || null,
      extra_stop: extraStop || null,
      is_public: isPublic,
      region,
      city,
      distance: routeMetrics?.distance,
      osm_route_data: routeMetrics ? {
        distance: routeMetrics.distance,
        duration: routeMetrics.duration,
        polyline: routeMetrics.polyline || ""
      } : undefined
    });

    if (showToast) {
      // success toast is handled by mutation
    }
  };

  const handleSaveRouteNew = async () => {
    const isPublic = existingRoute?.is_public ?? false;
    performSave(isPublic);
  };

  const handleSharePublicNew = () => {
    if (startAddress || endAddress) {
      setShowPrivacyWarning(true);
    } else {
      savePublicRouteNew();
    }
  };

  const savePublicRouteNew = async () => {
    setShowPrivacyWarning(false);
    if (selectedStops.length < 2) {
      toast({
        title: t("routeBuilder.tooFewShopsTitle"),
        description: t("routeBuilder.tooFewShopsDesc"),
        variant: "destructive",
      });
      return;
    }
    isSharingAction.current = true;
    performSave(true);
  };

  // Auto-save logic
  useEffect(() => {
    if (routeChanged && name && selectedStops.length >= 2 && !saveRouteMutation.isPending) {
      const timer = setTimeout(() => {
        const isPublic = existingRoute?.is_public ?? false;
        performSave(isPublic, false); // No toast for auto-save
      }, 5000); // Debounce save for 5 seconds
      return () => clearTimeout(timer);
    }
  }, [routeChanged, name, selectedStops, startAddress, endAddress, extraStop, description]);

  // Handle auto-save on unmount
  useEffect(() => {
    return () => {
      if (routeChanged && name && selectedStops.length >= 2 && !saveRouteMutation.isPending) {
        const isPublic = existingRoute?.is_public ?? false;
        // Fire and forget save on unmount if dirty
        performSave(isPublic, false);
      }
    };
  }, [routeChanged, name, selectedStops, existingRoute]);

  // Effect for syncing Draft Route from context on first load
  useEffect(() => {
    if (!editRouteId && !hasPopulated && preSelectedShops.length > 0) {
      setSelectedStops(preSelectedShops);
      if (persistedStart) setStartAddress(persistedStart);
      if (persistedEnd) setEndAddress(persistedEnd);
      if (persistedExtraStop) setExtraStop(persistedExtraStop);
      setHasPopulated(true); // Don't overwrite again
      setRouteChanged(false);
    }
  }, [editRouteId, hasPopulated, preSelectedShops, persistedStart, persistedEnd, persistedExtraStop]);

  // Effect for saving local edits to Draft Route context
  useEffect(() => {
    if (!editRouteId) {
      setContextShops(selectedStops);
    }
  }, [selectedStops, editRouteId, setContextShops]);

  const markRouteDirty = () => {
    setRouteChanged(true);
    setNeedsRecalculation(true);
    // Avoid showing stale polyline/metrics after edits until user explicitly recalculates.
    setRouteMetrics(null);
  };

  // Effect for debounced recalculation when stops change (e.g. via drag-and-drop)
  useEffect(() => {
    if (isInsider && selectedStops.length >= 2 && needsRecalculation) {
      const timer = setTimeout(() => {
        handleCalculateMetrics(false);
      }, 1500); // 1.5s debounce to avoid flickering while dragging
      return () => clearTimeout(timer);
    }
  }, [selectedStops, isInsider, needsRecalculation]);

  // Effect for populating route data when editing an existing route
  useEffect(() => {
    if (existingRoute && !hasPopulated && !showMergePrompt) {
      if (preSelectedShops.length > 0 && !isCopiedRoute) {
        setShowMergePrompt(true);
      } else {
        setName(existingRoute.name);
        setDescription(existingRoute.description || "");
        if (existingRoute.stopsData) {
          const uniqueStops = [];
          const seenIds = new Set();
          for (const s of existingRoute.stopsData) {
            if (!seenIds.has(s.id)) {
              seenIds.add(s.id);
              uniqueStops.push(s);
            }
          }
          setSelectedStops(uniqueStops);
        }
        setStartAddress(existingRoute.start_address);
        setEndAddress(existingRoute.end_address);
        setExtraStop(existingRoute.extra_stop);

        const distance = existingRoute.osm_route_data?.distance || existingRoute.distance;
        const duration = existingRoute.osm_route_data?.duration;
        const polyline = existingRoute.osm_route_data?.polyline;
        if (distance || polyline) {
          setRouteMetrics({
            distance: distance || "",
            duration: duration || "",
            polyline: polyline || ""
          });
        }

        setHasPopulated(true);
        setRouteChanged(false);

        if (!isInsider) {
          setAutoOptimizeEnabled(false);
        }
      }
    }
  }, [existingRoute, hasPopulated, showMergePrompt, preSelectedShops.length, isInsider, isCopiedRoute]);

  // Handle "Optimize for Me" (Insider feature)
  const handlePersonalizeForMe = async () => {
    if (!user?.homeAddress) {
      toast({
        title: t("routeBuilder.noHomeTitle"),
        description: t("routeBuilder.noHomeDesc"),
        variant: "destructive"
      });
      return;
    }

    const homeAddr: AddressSelection = {
      source: "custom",
      address: user.homeAddress,
      lat: user.homeLat ?? undefined,
      lng: user.homeLng ?? undefined
    };

    setStartAddress(homeAddr);
    setEndAddress(homeAddr);
    setRouteChanged(true);
    setNeedsRecalculation(true);

    toast({
      title: t("routeBuilder.personalizingTitle"),
      description: t("routeBuilder.personalizingDesc")
    });

    setPersistedStart(homeAddr);
    setPersistedEnd(homeAddr);

    setTimeout(() => {
      if (selectedStops.length >= 3) {
        handleOptimizeRoute();
      } else {
        handleCalculateMetrics(true);
      }
    }, 100);
  };

  // Handle autoOptimize parameter from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('autoOptimize') === 'true' && isLoggedIn && isInsider && selectedStops.length > 0) {
      // Remove param to prevent repeat triggers
      const url = new URL(window.location.href);
      url.searchParams.delete('autoOptimize');
      window.history.replaceState({}, '', url.toString());

      setTimeout(() => {
        if (selectedStops.length >= 3) {
          handleOptimizeRoute();
        } else {
          handleCalculateMetrics(true);
        }
      }, 500);
    }
  }, [isLoggedIn, isInsider, selectedStops.length]);

  // Auto-populate home address only for NEW routes and if Insider and NO persisted address exists
  useEffect(() => {
    if (!editRouteId && !hasAutoPopulatedHome && !persistedStart && user?.homeAddress && isInsider) {
      const homeAddr: AddressSelection = {
        source: "custom",
        address: user.homeAddress,
        lat: user.homeLat ?? undefined,
        lng: user.homeLng ?? undefined
      };
      setStartAddress(homeAddr);
      setEndAddress(homeAddr);
      markRouteDirty();
      setPersistedStart(homeAddr);
      setPersistedEnd(homeAddr);
      setHasAutoPopulatedHome(true);

      toast({
        title: t("routeBuilder.homeSetTitle"),
        description: t("routeBuilder.homeSetDesc")
      });
    } else if (persistedStart) {
      setHasAutoPopulatedHome(true);
    }
  }, [editRouteId, hasAutoPopulatedHome, user?.homeAddress, tier, toast, persistedStart, setPersistedStart, setPersistedEnd]);

  // Handle URL-based personalization (from Route Detail page)
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('personalize') === 'true' && isLoggedIn && isInsider && !hasAutoPopulatedHome) {
      handlePersonalizeForMe();
    }
  }, [isLoggedIn, isInsider, hasAutoPopulatedHome]);

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto text-center py-16">
            <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-bold mb-2">{t("routeBuilder.loginTitle")}</h2>
            <p className="text-muted-foreground mb-6">
              {t("routeBuilder.loginDesc")}
            </p>
            <Link href="/account">
              <Button>{t("routeBuilder.toAccountPage")}</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const addStop = (shop: Shop) => {
    if (selectedStops.length >= maxStopsPerRoute) {
      setUpgradeVariant("routes");
      setShowUpgradeModal(true);
      return;
    }
    setSelectedStops([...selectedStops, shop]);
    markRouteDirty();
  };

  const removeStop = (index: number) => {
    setSelectedStops(selectedStops.filter((_, i) => i !== index));
    markRouteDirty();
  };

  const handleReverseRoute = () => {
    if (selectedStops.length < 2) return;

    // Reverse shops
    setSelectedStops(prev => [...prev].reverse());

    // Swap addresses
    const oldStart = startAddress;
    const oldEnd = endAddress;
    setStartAddress(oldEnd);
    setEndAddress(oldStart);

    // Wipe old polyline — it was for the old direction and is now stale.
    // The map will recalculate automatically below.
    setRouteMetrics(null);
    setRouteChanged(true);
    setNeedsRecalculation(true);

    const hasSpecificAddresses = oldStart || oldEnd;

    toast({
      title: t("routeBuilder.reversedTitle"),
      description: hasSpecificAddresses
        ? t("routeBuilder.reversedWithAddressesDesc") || "Route en adressen zijn omgedraaid!"
        : t("routeBuilder.reversedDesc")
    });

    // Auto-recalculate the route for the reversed stop order.
    // Only for Insiders/Power users — free users see the static thumbnail only.
    if (isInsider) {
      setTimeout(() => {
        handleCalculateMetrics(true);
      }, 250);
    }
  };

  const handleClearEverything = () => {
    if (window.confirm(t("routeBuilder.clearConfirm"))) {
      clearRoute();
      setSelectedStops([]);
      setStartAddress(null);
      setEndAddress(null);
      setName("");
      setDescription("");
      setRouteMetrics(null);
      setRouteChanged(false);
      setNeedsRecalculation(false);

      toast({
        title: t("routeBuilder.clearedTitle"),
        description: t("routeBuilder.clearedDesc")
      });
    }
  };

  const handleClearShops = () => {
    if (window.confirm(t("routeBuilder.clearShopsConfirm") || "Weet je zeker dat je alle winkels wilt verwijderen?")) {
      setSelectedStops([]);
      setRouteMetrics(null);
      markRouteDirty();
      toast({
        title: t("routeBuilder.clearedTitle"),
        description: t("routeBuilder.clearedDesc")
      });
    }
  };

  const handleAddressChange = (type: 'start' | 'end' | 'extra', val: AddressSelection | null) => {
    if (type === 'start') setStartAddress(val);
    else if (type === 'end') setEndAddress(val);
    else setExtraStop(val);
    markRouteDirty();
  };

  const handleOptimizeRoute = async () => {
    if (selectedStops.length < 3) {
      toast({ title: t("routeBuilder.tooFewStopsTitle"), description: t("routeBuilder.tooFewStopsDesc") });
      return;
    }

    setIsOptimizing(true);

    if (isInsider) {
      const { canProceed, limit, period } = checkUsageLimit('optimization', tier);

      if (!canProceed) {
        toast({
          title: period === 'daily' ? t("routeBuilder.dailyLimitTitle") : t("routeBuilder.monthlyLimitTitle"),
          description: period === 'daily' ? t("routeBuilder.dailyLimitDesc", { limit }) : t("routeBuilder.monthlyLimitDesc", { limit }),
          variant: "destructive"
        });
        setIsOptimizing(false);
        return;
      }
      incrementUsage('optimization');
    }

    try {
      const hasCustomStart = !!startAddress;
      const hasCustomEnd = !!endAddress;

      const startIndex = hasCustomStart ? 0 : 1;
      const endIndex = hasCustomEnd ? selectedStops.length : selectedStops.length - 1;

      const intermediateShops = selectedStops.slice(startIndex, endIndex);
      const validIntermediates = intermediateShops.filter(s => s.lat != null && s.lng != null);
      const invalidIntermediates = intermediateShops.filter(s => s.lat == null || s.lng == null);

      if (validIntermediates.length < 2) {
        toast({ title: t("routeBuilder.alreadyOptimalTitle"), description: t("routeBuilder.alreadyOptimalDesc") });
        setIsOptimizing(false);
        return;
      }

      let origin: string | { lat: number; lng: number };
      if (hasCustomStart) {
        origin = (startAddress.lat && startAddress.lng) ? { lat: startAddress.lat, lng: startAddress.lng } : startAddress.address || "";
      } else {
        const s = selectedStops[0];
        origin = (s.lat && s.lng) ? { lat: s.lat, lng: s.lng } : s.address;
      }

      let destination: string | { lat: number; lng: number };
      if (hasCustomEnd) {
        destination = (endAddress.lat && endAddress.lng) ? { lat: endAddress.lat, lng: endAddress.lng } : endAddress.address || "";
      } else {
        const s = selectedStops[selectedStops.length - 1];
        destination = (s.lat && s.lng) ? { lat: s.lat, lng: s.lng } : s.address;
      }

      const waypointsArg = validIntermediates.map(s => ({ lat: s.lat as number, lng: s.lng as number }));

      if (extraStop) {
        if (extraStop.lat != null && extraStop.lng != null) {
          waypointsArg.push({ lat: extraStop.lat as number, lng: extraStop.lng as number });
        } else if (extraStop.address) {
          // Google API handles string strings too but our interface needs LatLng.
          // Fallback to address string if possible in generateRoute (which usually takes array if using strings...)
          // As a workaround, we'll cast to any for Address string in waypoint.
          waypointsArg.push({ location: extraStop.address } as any);
        }
      }

      const coords: [number, number][] = [
        [typeof origin === 'string' ? 0 : origin.lng, typeof origin === 'string' ? 0 : origin.lat] as [number, number],
        ...waypointsArg.map(w => [w.lng, w.lat] as [number, number]),
        [typeof destination === 'string' ? 0 : destination.lng, typeof destination === 'string' ? 0 : destination.lat] as [number, number]
      ].filter(c => c[0] !== 0 || c[1] !== 0) as [number, number][];

      // We MUST pass fixStartEnd = true to the OSM generator, because the re-assembly logic 
      // below strictly assumes the result.waypointOrder contains only the *intermediate* waypoint indices.
      // If fixStartEnd was false, it returned indices for the entire list and broke the mapping.
      const fixStartEnd = true;

      const result = await generateOSMRoute(coords, 'NL', true, fixStartEnd);

      if (result && result.waypointOrder) {
        // Re-order the intermediate stops according to the TSP result
        const optimizedIntermediates = result.waypointOrder
          .map(idx => validIntermediates[idx])
          .filter(Boolean);

        // Build the final ordered stop list:
        //  - If there is NO custom start, the first shop is the fixed origin
        //    (it was passed as origin to TSP, not as a waypoint, so it doesn't
        //     appear in waypointOrder)
        //  - Same logic for the end shop when there is no custom end.
        const newStops: typeof selectedStops = [];

        if (!hasCustomStart) {
          // selectedStops[0] was used as the origin — put it back first
          newStops.push(selectedStops[0]);
        }

        newStops.push(...optimizedIntermediates);
        newStops.push(...invalidIntermediates); // shops with no coordinates get appended

        if (!hasCustomEnd) {
          // selectedStops[last] was used as the destination — put it back last
          newStops.push(selectedStops[selectedStops.length - 1]);
        }

        setSelectedStops(newStops);
        setRouteChanged(true);  // mark dirty so auto-save picks it up
        setNeedsRecalculation(false);
        setRouteMetrics({
          distance: result.distance.text,
          duration: result.duration.text,
          polyline: result.polyline
        });

        toast({ title: t("routeBuilder.optimizedTitle"), description: `${result.distance.text} - ${result.duration.text}` });
      }
    } catch (error) {
      console.error(error);
      toast({ title: t("common.error"), description: t("routeBuilder.optimizeFailedDesc"), variant: "destructive" });
    } finally {
      setIsOptimizing(false);
    }
  };

  const lastRequestRef = useRef<string>("");

  const handleCalculateMetrics = async (force: boolean = false) => {
    if (selectedStops.length === 0) return;
    setIsCalculating(true);

    try {
      const hasCustomStart = !!startAddress;
      const hasCustomEnd = !!endAddress;

      let origin: string | { lat: number; lng: number };
      if (hasCustomStart) {
        origin = (startAddress.lat && startAddress.lng) ? { lat: startAddress.lat, lng: startAddress.lng } : startAddress.address || "";
      } else {
        const s = selectedStops[0];
        origin = s ? ((s.lat && s.lng) ? { lat: s.lat, lng: s.lng } : s.address) : "";
      }

      let originCoord: { lat: number; lng: number } | null = null;
      let destCoord: { lat: number; lng: number } | null = null;

      if (hasCustomStart && startAddress?.lat != null && startAddress?.lng != null) {
        originCoord = { lat: startAddress.lat as number, lng: startAddress.lng as number };
      } else if (!hasCustomStart && selectedStops[0]?.lat != null && selectedStops[0]?.lng != null) {
        originCoord = { lat: selectedStops[0].lat as number, lng: selectedStops[0].lng as number };
      }

      if (hasCustomEnd && endAddress?.lat != null && endAddress?.lng != null) {
        destCoord = { lat: endAddress.lat as number, lng: endAddress.lng as number };
      } else if (!hasCustomEnd && selectedStops.length > 0 && selectedStops[selectedStops.length - 1]?.lat != null && selectedStops[selectedStops.length - 1]?.lng != null) {
        const lastShop = selectedStops[selectedStops.length - 1];
        destCoord = { lat: lastShop.lat as number, lng: lastShop.lng as number };
      }

      if (!originCoord || !destCoord) return;

      const intermediateShops = selectedStops.slice(
        hasCustomStart ? 0 : 1,
        hasCustomEnd ? selectedStops.length : selectedStops.length - 1
      );
      const waypointsArg = intermediateShops
        .filter(s => s.lat != null && s.lng != null)
        .map(s => ({ lat: s.lat as number, lng: s.lng as number }));

      if (extraStop && extraStop.lat != null && extraStop.lng != null) {
        waypointsArg.push({ lat: extraStop.lat as number, lng: extraStop.lng as number });
      }

      const fingerprint = JSON.stringify({
        origin: originCoord,
        destination: destCoord,
        waypoints: waypointsArg,
        optimize: false
      });

      if (!force && lastRequestRef.current === fingerprint) return;
      lastRequestRef.current = fingerprint;

      const coords: [number, number][] = [];
      coords.push([originCoord.lng, originCoord.lat]);
      coords.push(...waypointsArg.map(w => [w.lng, w.lat] as [number, number]));
      coords.push([destCoord.lng, destCoord.lat]);

      const result = await generateOSMRoute(coords, 'NL', false);
      if (result) {
        setRouteMetrics({
          distance: result.distance.text,
          duration: result.duration.text,
          polyline: result.polyline
        });
        setNeedsRecalculation(false);
      }
    } catch (error) {
      console.error("Failed to calculate metrics:", error);
    } finally {
      setIsCalculating(false);
    }
  };

  useEffect(() => {
    setPersistedStart(startAddress);
    setPersistedEnd(endAddress);
    setPersistedExtraStop(extraStop);
  }, [startAddress, endAddress, extraStop, setPersistedStart, setPersistedEnd, setPersistedExtraStop]);

  const generateMapsUrl = () => {
    let originParam = "";
    if (startAddress) {
      if (startAddress.lat && startAddress.lng) {
        originParam = `&origin=${startAddress.lat},${startAddress.lng}`;
      } else if (startAddress.address) {
        originParam = `&origin=${encodeURIComponent(startAddress.address)}`;
      }
    }

    const points: string[] = [];

    // All shops
    const shopAddresses = selectedStops.map(shop => encodeURIComponent(`${shop.name}, ${shop.city}`));
    points.push(...shopAddresses);

    if (extraStop) {
      if (extraStop.lat != null && extraStop.lng != null) {
        points.push(`${extraStop.lat},${extraStop.lng}`);
      } else if (extraStop.address) {
        points.push(encodeURIComponent(extraStop.address));
      }
    }

    // End address
    if (endAddress) {
      if (endAddress.lat != null && endAddress.lng != null) {
        points.push(`${endAddress.lat},${endAddress.lng}`);
      } else if (endAddress.address) {
        points.push(encodeURIComponent(endAddress.address));
      }
    }

    if (points.length === 0) return "#";

    const destination = points.pop()!;
    const waypoints = points.join("|");
    const waypointsParam = waypoints ? `&waypoints=${waypoints}` : "";

    const mapsUrl = `https://www.google.com/maps/dir/?api=1${originParam}&destination=${destination}${waypointsParam}`;
    return mapsUrl;
  };



  const isOverLimit = tier === "free" && selectedStops.length > FREE_TIER_LIMITS.maxStopsPerRoute;

  useEffect(() => {
    if (highlightedStopId) {
      const timer = setTimeout(() => {
        setHighlightedStopId(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightedStopId]);

  return (
    <div className="min-h-screen flex flex-col bg-background">

      <main className="flex-1 container mx-auto px-4 py-8">
        {showMergePrompt && (
          <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-background rounded-lg shadow-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-bold mb-4">{t("routeBuilder.mergePrompt.title")}</h2>
              <p className="text-muted-foreground mb-6">
                {t("routeBuilder.mergePrompt.description", {
                  count: preSelectedShops.length,
                  name: existingRoute?.name
                })}
              </p>
              <div className="flex flex-col gap-3">
                <Button onClick={() => handleMergeAction('merge')} className="w-full">{t("routeBuilder.mergePrompt.yes")}</Button>
                <Button onClick={() => handleMergeAction('overwrite')} variant="secondary" className="w-full">{t("routeBuilder.mergePrompt.no")}</Button>
                <Button onClick={() => handleMergeAction('cancel')} variant="ghost" className="w-full">{t("common.cancel")}</Button>
              </div>
            </div>
          </div>
        )}

        <Link href="/routes">
          <Button variant="ghost" className="mb-6" data-testid="button-back">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("routeBuilder.backToRoutes")}
          </Button>
        </Link>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">{editRouteId ? t("routeBuilder.editRoute") : t("routeBuilder.newRoute")}</h1>
          {tier === "free" && (
            <Badge variant="outline">
              Stops: {selectedStops.length}/{FREE_TIER_LIMITS.maxStopsPerRoute}
            </Badge>
          )}
        </div>

        {isOverLimit && (
          <div className="mb-6">
            <LimitBanner
              current={selectedStops.length}
              max={FREE_TIER_LIMITS.maxStopsPerRoute}
              type="stops"
              onUpgrade={() => {
                setUpgradeVariant("routes");
                setShowUpgradeModal(true);
              }}
            />
          </div>
        )}

        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="lg:col-span-1 order-1 lg:order-2 space-y-4">
              <Card className="h-auto flex flex-col">
                <CardHeader>
                  <CardTitle className="text-lg">{t("routeBuilder.mapTitle")}</CardTitle>
                </CardHeader>
                <CardContent className="aspect-square p-0 overflow-hidden lg:rounded-b-lg relative">
                  <OSMRouteMap
                    shops={selectedStops}
                    startAddress={startAddress}
                    endAddress={endAddress}
                    extraStop={extraStop}
                    polyline={routeMetrics?.polyline}
                    height="100%"
                    onShopClick={(shopId, index) => {
                      const targetId = `selected-stop-${shopId}-${index}`;
                      const element = document.getElementById(targetId);
                      if (element) {
                        requestAnimationFrame(() => {
                          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          setHighlightedStopId(`${shopId}-${index}`);
                        });
                      }
                    }}
                  />
                </CardContent>

                {(!routeMetrics?.polyline || needsRecalculation) && selectedStops.length >= 2 && (
                  <div className="p-4 border-t mt-auto bg-muted/10">
                    <Button
                      className="w-full text-base font-medium h-12"
                      onClick={() => {
                        if (isInsider && selectedStops.length >= 3) {
                          handleOptimizeRoute();
                        } else {
                          handleCalculateMetrics(true);
                        }
                      }}
                      disabled={isCalculating || isOptimizing}
                    >
                      {isCalculating || isOptimizing ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Navigation className="h-5 w-5 mr-2" />}
                      {isInsider && selectedStops.length >= 3
                        ? (t("routeBuilder.optimizeOrder") || "Optimaliseer route")
                        : (t("routeBuilder.calculateRoute") || "Toon route op kaart")}
                    </Button>
                  </div>
                )}

                <div className="p-4 lg:hidden border-t mt-auto">
                  <a
                    href={generateMapsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    onClick={() => { if (editRouteId) trackRouteEvent(editRouteId, "maps_click"); }}
                  >
                    <Button className="w-full h-12 text-base shadow-sm" variant="outline" disabled={selectedStops.length === 0}>
                      <ExternalLink className="h-5 w-5 mr-2" />
                      {t("routeBuilder.openMapsRoute")}
                    </Button>
                  </a>
                </div>
              </Card>
            </div>

            <div className="lg:col-span-1 order-2 lg:order-1 space-y-6">
              <Card className="flex flex-col h-full min-h-[500px]">
                <CardHeader className="flex-shrink-0">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span>{t("routeBuilder.selectedStops")}</span>
                    <div className="flex items-center gap-2">
                      {selectedStops.length >= 2 && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-muted-foreground"
                            onClick={handleReverseRoute}
                            title={t("routeBuilder.reverseRoute") || "Draai volgorde om"}
                          >
                            <ArrowUpDown className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={handleClearShops}
                            title={t("routeBuilder.clearShops") || "Verwijder alle winkels"}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      <Badge variant="secondary">{selectedStops.length}</Badge>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto space-y-4 relative">
                  {selectedStops.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground bg-muted/20 rounded-lg border-2 border-dashed border-border mb-4">
                      <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm px-4">{t("routeBuilder.noStopsYet")}</p>
                      <div className="mt-4 space-y-2 px-6">
                        <Link href="/kringloopwinkels">
                          <Button variant="outline" size="sm" className="w-full">{t("routeBuilder.searchShops")}</Button>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between p-2 bg-muted/30 rounded-md sticky top-0 bg-background z-10 border-b">
                        <Label className="text-xs flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {t("routeBuilder.day")}:
                        </Label>
                        <Select
                          value={selectedDay.toString()}
                          onValueChange={(v) => setSelectedDay(parseInt(v))}
                        >
                          <SelectTrigger className="h-8 w-[120px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">{t("shops.filters.day.monday")}</SelectItem>
                            <SelectItem value="1">{t("shops.filters.day.tuesday")}</SelectItem>
                            <SelectItem value="2">{t("shops.filters.day.wednesday")}</SelectItem>
                            <SelectItem value="3">{t("shops.filters.day.thursday")}</SelectItem>
                            <SelectItem value="4">{t("shops.filters.day.friday")}</SelectItem>
                            <SelectItem value="5">{t("shops.filters.day.saturday")}</SelectItem>
                            <SelectItem value="6">{t("shops.filters.day.sunday")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Reorder.Group
                        axis="y"
                        values={selectedStops}
                        onReorder={(newOrder) => {
                          setSelectedStops(newOrder);
                          markRouteDirty();
                        }}
                        className="space-y-2"
                      >
                        {selectedStops.map((shop, index) => {
                          const openingStatus = shop.openingHoursStructured
                            ? getOpeningStatus(
                              shop.openingHoursStructured as OpeningHourEntry[],
                              selectedDay,
                              {
                                isClosed: !!shop.is_temporarily_closed,
                                until: shop.temporarily_closed_until,
                                reason: shop.temporarily_closed_reason
                              },
                              { locale, t }
                            )
                            : null;

                          return (
                            <Reorder.Item
                              key={shop.id}
                              value={shop}
                              id={`selected-stop-${shop.id}-${index}`}
                              className={`flex items-center gap-2 p-3 border transition-all duration-200 rounded-lg cursor-grab active:cursor-grabbing ${highlightedStopId === `${shop.id}-${index}`
                                ? 'bg-primary/20 border-primary shadow-md scale-[1.02]'
                                : 'bg-card border-border'
                                }`}
                              data-testid={`selected-stop-${shop.id}`}
                            >
                              <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                {index + 1}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-xs truncate">{shop.name}</p>
                                  {openingStatus && (
                                    <Badge
                                      variant={openingStatus.isOpen ? "default" : "secondary"}
                                      className={`text-[9px] px-1 py-0 h-3.5 ${openingStatus.isOpen
                                        ? 'bg-green-500/10 text-green-700 border-green-200'
                                        : 'bg-red-500/10 text-red-700 border-red-200'
                                        }`}
                                    >
                                      {openingStatus.isOpen ? t("shops.filters.openNow") : t("common.closed")}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-[10px] text-muted-foreground">{shop.city}</p>
                                {openingStatus && (
                                  <p className={`text-[10px] font-medium mt-0.5 ${openingStatus.colorClass}`}>
                                    {openingStatus.status}
                                  </p>
                                )}
                              </div>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-muted-foreground hover:bg-red-50 hover:text-red-600 transition-colors"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  removeStop(index);
                                }}
                                data-testid={`button-remove-${shop.id}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </Reorder.Item>
                          );
                        })}
                      </Reorder.Group>

                      {isInsider && (
                        <div className="pt-2 space-y-2">
                          <Link href="/kringloopwinkels">
                            <Button variant="outline" size="sm" className="w-full border-primary/20 text-primary hover:bg-primary/10 bg-primary/5 font-medium">
                              <Plus className="h-3.5 w-3.5 mr-2" />
                              {t("routeBuilder.addShop")}
                            </Button>
                          </Link>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
                <CardContent className="pt-4 border-t border-border flex-shrink-0">
                  <a
                    href={generateMapsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    onClick={() => { if (editRouteId) trackRouteEvent(editRouteId, "maps_click"); }}
                  >
                    <Button className="w-full font-bold shadow-sm" variant="default" disabled={selectedStops.length === 0}>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {t("routeBuilder.openGoogleMaps")}
                    </Button>
                  </a>
                </CardContent>
              </Card>
              {needsRecalculation && isInsider && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <p>{t("routeBuilder.recalculatePrompt")}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-400 bg-white hover:bg-amber-100"
                      onClick={() => handleCalculateMetrics(true)}
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-2" />
                      {t("routeBuilder.recalculate")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t("routeBuilder.detailsTitle")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("routeBuilder.nameLabel")}</Label>
                  <Input
                    id="name"
                    placeholder={t("routeBuilder.namePlaceholder")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div className="flex flex-row items-center justify-around py-3 bg-muted/20 rounded-lg border px-4 gap-2 sm:gap-4">
                  <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
                    <span className="text-[10px] sm:text-xs text-muted-foreground block">{t("routeBuilder.distance")}</span>
                    <span className="font-bold text-xs sm:text-base flex items-center gap-1">
                      <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-primary" />
                      {routeMetrics?.distance || '--'}
                    </span>
                  </div>

                  <div className="w-px h-8 bg-border hidden sm:block" />

                  <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
                    <span className="text-[10px] sm:text-xs text-muted-foreground block">{t("routeBuilder.travelTime")}</span>
                    <span className="font-bold text-xs sm:text-base flex items-center gap-1">
                      <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-primary" />
                      {routeMetrics?.duration || '--'}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">{t("routeBuilder.descriptionOptional")}</Label>
                  <Textarea
                    id="description"
                    placeholder={t("routeBuilder.descriptionPlaceholder")}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="pt-4 border-t border-border flex flex-col gap-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={selectedStops.length < 2}
                      onClick={handleReverseRoute}
                    >
                      <ArrowUpDown className="h-4 w-4 mr-2" />
                      {t("routeBuilder.reverse")}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      disabled={selectedStops.length === 0 && !startAddress && !endAddress}
                      onClick={handleClearEverything}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t("routeBuilder.clear")}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={!name || selectedStops.length < 2 || saveRouteMutation.isPending}
                    onClick={handleSaveRouteNew}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saveRouteMutation.isPending ? t("routeBuilder.saving") : t("routeBuilder.save")}
                  </Button>
                  <Button
                    className="w-full"
                    disabled={!name || selectedStops.length < 2 || saveRouteMutation.isPending || (isCopiedRoute && copiedRouteDiffCount < 3)}
                    onClick={handleSharePublicNew}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    {t("routeBuilder.sharePublic")}
                  </Button>
                </div>
                {isCopiedRoute && copiedRouteDiffCount < 3 && (
                  <p className="text-[10px] text-muted-foreground text-center mt-1 text-amber-600">
                    Pas minimaal 3 winkels aan om deze gekopieerde route publiek te delen (nog {3 - copiedRouteDiffCount} vereist).
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Home className="h-5 w-5" />
                  {t("routeBuilder.startEndTitle")}
                  {!isInsider && (
                    <Badge variant="outline" className="text-xs ml-auto">
                      <Lock className="h-3 w-3 mr-1" />
                      {t("account.tier.insider")}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <CustomAddressSelector
                    label={t("routeBuilder.startAddressOptional")}
                    value={startAddress}
                    onChange={(val) => handleAddressChange('start', val)}
                    homeAddress={user ? {
                      address: user.homeAddress || "",
                      city: user.homeCity || "",
                      lat: user.homeLat || undefined,
                      lng: user.homeLng || undefined
                    } : null}
                    disabled={!isInsider}
                  />
                  <p className="text-[10px] sm:text-xs text-muted-foreground leading-tight px-1 pt-1 opacity-80">
                    * {t("routeBuilder.startAddressHelp") || "Dit startadres dient puur voor het berekenen van de route. Zodra je de route opent, navigeert Maps altijd vanaf je huidige locatie. Lees meer hierover in de FAQ."}
                  </p>
                </div>

                <CustomAddressSelector
                  label="Tussenstop (optioneel, max 1)"
                  value={extraStop}
                  onChange={(val) => handleAddressChange('extra', val)}
                  homeAddress={null} // Don't allow 'home' for extra stop logic easily or let it? Set null.
                  disabled={false}
                />

                <CustomAddressSelector
                  label={t("routeBuilder.endAddressOptional")}
                  value={endAddress}
                  onChange={(val) => handleAddressChange('end', val)}
                  homeAddress={user ? {
                    address: user.homeAddress || "",
                    city: user.homeCity || "",
                    lat: user.homeLat || undefined,
                    lng: user.homeLng || undefined
                  } : null}
                  disabled={!isInsider}
                />

                <div className="pt-4 border-t border-border space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-medium">{t("routeBuilder.autoOptimize")}</Label>
                      {!isInsider && <Badge variant="outline" className="text-[10px] px-1 h-4">{t("account.tier.insider")}</Badge>}
                    </div>
                    <Switch
                      checked={autoOptimizeEnabled}
                      onCheckedChange={setAutoOptimizeEnabled}
                      disabled={!isInsider}
                    />
                  </div>

                  {isInsider ? (
                    <Button
                      className="w-full bg-primary/10 text-primary border-primary/20 hover:bg-primary/20"
                      onClick={handlePersonalizeForMe}
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      {t("routeBuilder.useHomeOptimize")}
                    </Button>
                  ) : null}

                  <Button
                    variant="secondary"
                    className="w-full mb-2"
                    disabled={!isInsider || isOptimizing || !needsRecalculation || selectedStops.length < 3}
                    onClick={handleOptimizeRoute}
                  >
                    {isOptimizing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 mr-2" />
                    )}
                    {t("routeBuilder.optimizeOrder")}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-muted-foreground"
                    onClick={() => handleCalculateMetrics(true)}
                    disabled={selectedStops.length < 2}
                  >
                    <RefreshCw className={`h-3 w-3 mr-2 ${isOptimizing ? 'animate-spin' : ''}`} />
                    {t("routeBuilder.recalculateInfo")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main >

      <Footer />

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        variant={upgradeVariant}
      />

      <PrivacyWarningDialog
        open={showPrivacyWarning}
        onOpenChange={setShowPrivacyWarning}
        onConfirm={savePublicRouteNew}
        hasStartAddress={!!startAddress}
        hasEndAddress={!!endAddress}
      />
    </div >
  );
}
